import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * GridComponent - A uniform wrapper for react-grid-layout compatibility
 * 
 * This component provides a consistent interface for all components to work with react-grid-layout.
 * It handles the drag handle, resize behavior, and provides consistent styling.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component content to wrap
 * @param {string} props.title - Title displayed in the header (also used as drag handle)
 * @param {boolean} props.showHeader - Whether to show the header with title and drag handle (default: true)
 * @param {boolean} props.resizable - Whether the component can be resized (default: true)
 * @param {boolean} props.draggable - Whether the component can be dragged (default: true)
 * @param {string} props.className - Additional CSS classes for the wrapper
 * @param {Object} props.headerActions - Additional actions to display in the header
 * @param {string} props.headerVariant - Header styling variant: 'default' | 'minimal' | 'transparent'
 * @param {Object} props.style - Additional inline styles
 * @param {function} props.onClose - Callback when close button is clicked (if provided, shows close button)
 */
const GridComponent = forwardRef(({
  children,
  title,
  showHeader = true,
  resizable = true,
  draggable = true,
  className = '',
  headerActions,
  headerVariant = 'default',
  style = {},
  onClose,
  ...otherProps
}, ref) => {
  const { t } = useTranslation('common');

  const getHeaderStyles = () => {
    switch (headerVariant) {
      case 'minimal':
        return 'bg-muted border-b border-border';
      case 'transparent':
        return 'bg-transparent';
      default:
        return 'bg-card border-b border-border';
    }
  };

  return (
    <div
      ref={ref}
      className={cn('bg-card rounded-lg shadow-sm border border-border flex flex-col h-full overflow-hidden', className)}
      style={style}
      {...otherProps}
    >
      {showHeader && (
        <div className={`flex-shrink-0 px-4 py-0.5 flex items-center justify-between ${getHeaderStyles()}`}>
          <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
            {draggable && (
              <div 
                className="grid-drag-handle cursor-grab hover:cursor-grabbing"
                style={{ 
                  padding: '4px', 
                  borderRadius: '4px'
                }}
                title={t('grid.dragHandleTooltip')}
              >
                <svg 
                  className="w-4 h-4 text-gray-400 flex-shrink-0 pointer-events-none" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M4 8a2 2 0 11-4 0 2 2 0 014 0zM4 14a2 2 0 11-4 0 2 2 0 014 0zM10 8a2 2 0 11-4 0 2 2 0 014 0zM10 14a2 2 0 11-4 0 2 2 0 014 0zM16 8a2 2 0 11-4 0 2 2 0 014 0zM16 14a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
              </div>
            )}
            {title && (
              <h3 className="text-sm font-medium text-gray-900 truncate select-none" title={title}>
                {title}
              </h3>
            )}
          </div>
          
          <div className="flex items-center gap-2 min-w-0">
            {headerActions}
            {onClose && (
              <button
                onClick={onClose}
                className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors p-1 rounded z-10 relative"
                title={t('grid.removeComponentTooltip')}
                style={{ pointerEvents: 'auto' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className={`flex-1 overflow-auto min-w-0 ${showHeader ? '' : 'p-4'}`}>
        {children}
      </div>
      
      {resizable && (
        <div className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize opacity-30 hover:opacity-60 transition-opacity">
          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M18 2L2 18M18 6L6 18M18 10L10 18"/>
          </svg>
        </div>
      )}
    </div>
  );
});

GridComponent.displayName = 'GridComponent';

export default GridComponent;

/**
 * Hook to provide default grid layout props for react-grid-layout items
 * 
 * @param {string} id - Unique identifier for the grid item
 * @param {Object} defaultLayout - Default layout configuration
 * @returns {Object} Layout configuration object
 */
export const useGridLayout = (id, defaultLayout = {}) => {
  return {
    i: id,
    x: defaultLayout.x || 0,
    y: defaultLayout.y || 0,
    w: defaultLayout.w || 6,
    h: defaultLayout.h || 4,
    minW: defaultLayout.minW || 3,
    minH: defaultLayout.minH || 2,
    maxW: defaultLayout.maxW || 12,
    maxH: defaultLayout.maxH || 12,
    resizeHandles: defaultLayout.resizeHandles || ['se', 'sw', 'nw', 'ne', 's', 'n', 'e', 'w'],
    ...defaultLayout
  };
};

/**
 * Pre-defined layout configurations for common component types
 */
export const GRID_LAYOUTS = {
  // Chart components
  histogram: { w: 8, h: 6, minW: 6, minH: 4 },
  chart: { w: 6, h: 4, minW: 4, minH: 3 },
  largeChart: { w: 12, h: 8, minW: 8, minH: 6 },
  
  // Card components
  card: { w: 4, h: 3, minW: 3, minH: 2 },
  wideCard: { w: 6, h: 3, minW: 4, minH: 2 },
  tallCard: { w: 4, h: 6, minW: 3, minH: 4 },
  
  // Table/list components
  table: { w: 8, h: 6, minW: 6, minH: 4 },
  list: { w: 4, h: 8, minW: 3, minH: 6 },
  
  // Graph/network components
  graph: { w: 12, h: 10, minW: 8, minH: 8 },
  
  // Control/settings components
  controls: { w: 4, h: 2, minW: 3, minH: 1 },
  settings: { w: 3, h: 4, minW: 2, minH: 3 }
};